import { RiCloseLine } from "react-icons/ri";

// Keys that hold array values
const MULTI_KEYS = new Set([
  "client",
  "project",
  "type",
  "status",
  "priority",
  "assignee",
  "reporter",
  "team",
]);

// Human-readable field labels
const FIELD_LABELS = {
  search: "Search",
  client: "Client",
  project: "Project",
  type: "Type",
  status: "Status",
  priority: "Priority",
  assignee: "Assignee",
  reporter: "Reporter",
  team: "Team",
};

/**
 * Resolves a filter value to its display label.
 * For UUID values, looks up the label in the provided option lists.
 * Falls back to the raw value string.
 */
function resolveLabel(value, optionMap) {
  if (!value) return null;
  const str = String(value);
  if (optionMap) {
    const match = optionMap.find(
      (o) => String(o.value ?? o.id ?? "") === str || String(o.label ?? "") === str
    );
    if (match) return match.label ?? str;
  }
  return str;
}

/**
 * Displays active filter values as dismissable chips.
 *
 * @param {object}   props.filter           – Current filter state object
 * @param {object}   [props.optionMaps]     – { fieldName: [{value, label}] } for resolving UUID labels
 * @param {function} props.onRemove         – (fieldName, value) => void
 */
const FilterChips = ({ filter = {}, optionMaps = {}, onRemove }) => {
  const chips = [];

  Object.entries(filter).forEach(([fieldName, fieldValue]) => {
    if (!FIELD_LABELS[fieldName]) return;
    const opts = optionMaps[fieldName] ?? null;

    if (MULTI_KEYS.has(fieldName)) {
      const arr = Array.isArray(fieldValue) ? fieldValue : [];
      arr.forEach((v) => {
        const label = resolveLabel(v, opts);
        if (label) {
          chips.push({ fieldName, value: v, label, fieldLabel: FIELD_LABELS[fieldName] });
        }
      });
    } else {
      const str = String(fieldValue ?? "").trim();
      if (str) {
        chips.push({
          fieldName,
          value: fieldValue,
          label: str,
          fieldLabel: FIELD_LABELS[fieldName],
        });
      }
    }
  });

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 px-1 pb-2">
      {chips.map(({ fieldName, value, label, fieldLabel }) => (
        <span
          key={`${fieldName}-${value}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-50 border border-blue-200 text-blue-800 rounded-full"
        >
          <span className="text-blue-500 font-semibold">{fieldLabel}:</span>
          {label}
          <button
            type="button"
            onClick={() => onRemove?.(fieldName, value)}
            className="ml-0.5 text-blue-400 hover:text-blue-600 rounded-full focus:outline-none"
            aria-label={`Remove ${fieldLabel}: ${label}`}
          >
            <RiCloseLine size={12} />
          </button>
        </span>
      ))}
    </div>
  );
};

export default FilterChips;
